"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useEffect, useCallback, type JSX } from "react";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { TabToggleWithOverflow } from "@/components/common/TabToggleWithOverflow";
import WaveHeader, { WaveHeaderPinnedSide } from "@/components/waves/header/WaveHeader";
import { WaveWinnersSmall } from "@/components/waves/winners/WaveWinnersSmall";
import BrainRightSidebarContent from "./BrainRightSidebarContent";
import BrainRightSidebarFollowers from "./BrainRightSidebarFollowers";
import { Mode, SidebarTab } from "./BrainRightSidebar";
import { WaveSmallLeaderboard } from "@/components/waves/small-leaderboard/WaveSmallLeaderboard";
import { WaveLeaderboardRightSidebarVoters } from "@/components/waves/leaderboard/sidebar/WaveLeaderboardRightSidebarVoters";
import { WaveLeaderboardRightSidebarActivityLogs } from "@/components/waves/leaderboard/sidebar/WaveLeaderboardRightSidebarActivityLogs";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
interface WaveContentProps {
  readonly wave: ApiWave;
  readonly mode: Mode;
  readonly setMode: (mode: Mode) => void;
  readonly activeTab: SidebarTab;
  readonly setActiveTab: (tab: SidebarTab) => void;
}
interface TabOption {
  key: SidebarTab;
  label: string;
}
export const WaveContent: React.FC<WaveContentProps> = t0 => {
  const $ = _c(72);
  const {
    wave,
    mode,
    setMode,
    activeTab,
    setActiveTab
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== mode || $[1] !== setMode) {
    t1 = () => setMode(mode === Mode.FOLLOWERS ? Mode.CONTENT : Mode.FOLLOWERS);
    $[0] = mode;
    $[1] = setMode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const onFollowersClick = t1;
  let t2;
  if ($[3] !== pathname || $[4] !== router || $[5] !== searchParams) {
    t2 = drop => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.set("drop", drop.id);
      router.push(`${pathname}?${params.toString()}`, {
        scroll: false
      });
    };
    $[3] = pathname;
    $[4] = router;
    $[5] = searchParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const onDropClick = t2;
  const isRankWave = wave.wave.type === ApiWaveType.Rank;
  const isApproveWave = wave.wave.type === ApiWaveType.Approve;
  const isCompetitionWave = isRankWave || isApproveWave;
  const {
    voting: t3,
    decisions: t4
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t3;
  const {
    firstDecisionDone
  } = t4;
  let t5;
  let t6;
  if ($[7] !== activeTab || $[8] !== firstDecisionDone || $[9] !== isApproveWave || $[10] !== isCompleted || $[11] !== setActiveTab) {
    t5 = () => {
      const isLeaderboardAndVotingEnded = activeTab === SidebarTab.LEADERBOARD && isCompleted && !isApproveWave;
      const isWinnersAndFirstDecisionNotPassed = activeTab === SidebarTab.WINNERS && !firstDecisionDone && !isApproveWave;
      if (isLeaderboardAndVotingEnded || isWinnersAndFirstDecisionNotPassed) {
        setActiveTab(SidebarTab.ABOUT);
      }
    };
    t6 = [isApproveWave, isCompleted, firstDecisionDone, activeTab, setActiveTab];
    $[7] = activeTab;
    $[8] = firstDecisionDone;
    $[9] = isApproveWave;
    $[10] = isCompleted;
    $[11] = setActiveTab;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      key: SidebarTab.ABOUT,
      label: "About"
    };
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let tabs;
  if ($[15] !== firstDecisionDone || $[16] !== isApproveWave || $[17] !== isCompleted) {
    tabs = [t7];
    if (!isCompleted || isApproveWave) {
      const t8 = isApproveWave ? "Approvals" : "Leaderboard";
      let t9;
      if ($[19] !== t8) {
        t9 = {
          key: SidebarTab.LEADERBOARD,
          label: t8
        };
        $[19] = t8;
        $[20] = t9;
      } else {
        t9 = $[20];
      }
      tabs.push(t9);
    }
    if (firstDecisionDone || isApproveWave) {
      const t8 = isApproveWave ? "Approved" : "Winners";
      let t9;
      if ($[21] !== t8) {
        t9 = {
          key: SidebarTab.WINNERS,
          label: t8
        };
        $[21] = t8;
        $[22] = t9;
      } else {
        t9 = $[22];
      }
      tabs.push(t9);
    }
    let t8;
    let t9;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        key: SidebarTab.TOP_VOTERS,
        label: "Voters"
      };
      t9 = {
        key: SidebarTab.ACTIVITY_LOG,
        label: "Activity"
      };
      $[23] = t8;
      $[24] = t9;
    } else {
      t8 = $[23];
      t9 = $[24];
    }
    tabs.push(t8, t9);
    $[15] = firstDecisionDone;
    $[16] = isApproveWave;
    $[17] = isCompleted;
    $[18] = tabs;
  } else {
    tabs = $[18];
  }
  const options = tabs;
  let t8;
  if ($[25] !== onFollowersClick || $[26] !== wave) {
    t8 = <WaveHeader wave={wave} onFollowersClick={onFollowersClick} useRing={false} useRounded={false} pinnedSide={WaveHeaderPinnedSide.LEFT} />;
    $[25] = onFollowersClick;
    $[26] = wave;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== mode || $[29] !== setMode || $[30] !== wave) {
    t9 = mode === Mode.CONTENT ? <BrainRightSidebarContent wave={wave} /> : <BrainRightSidebarFollowers wave={wave} closeFollowers={() => setMode(Mode.CONTENT)} />;
    $[28] = mode;
    $[29] = setMode;
    $[30] = wave;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t8 || $[33] !== t9) {
    t10 = <div className="tw-h-full tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-700">{t8}{t9}</div>;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== onDropClick || $[36] !== wave) {
    t11 = <div><WaveSmallLeaderboard wave={wave} onDropClick={onDropClick} /></div>;
    $[35] = onDropClick;
    $[36] = wave;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== onDropClick || $[39] !== wave) {
    t12 = <div><WaveWinnersSmall wave={wave} onDropClick={onDropClick} /></div>;
    $[38] = onDropClick;
    $[39] = wave;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== wave) {
    t13 = <div className="tw-p-4"><WaveLeaderboardRightSidebarVoters wave={wave} /></div>;
    $[41] = wave;
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  let t14;
  if ($[43] !== wave) {
    t14 = <div className="tw-p-4"><WaveLeaderboardRightSidebarActivityLogs wave={wave} /></div>;
    $[43] = wave;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== t14) {
    t15 = {
      [SidebarTab.ABOUT]: t10,
      [SidebarTab.LEADERBOARD]: t11,
      [SidebarTab.WINNERS]: t12,
      [SidebarTab.TOP_VOTERS]: t13,
      [SidebarTab.ACTIVITY_LOG]: t14
    };
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  const rankWaveComponents = t15;
  if (!isCompetitionWave) {
    let t16;
    if ($[51] !== onFollowersClick || $[52] !== wave) {
      t16 = <WaveHeader wave={wave} onFollowersClick={onFollowersClick} useRing={false} useRounded={false} pinnedSide={WaveHeaderPinnedSide.LEFT} />;
      $[51] = onFollowersClick;
      $[52] = wave;
      $[53] = t16;
    } else {
      t16 = $[53];
    }
    let t17;
    if ($[54] !== mode || $[55] !== setMode || $[56] !== wave) {
      t17 = mode === Mode.CONTENT ? <BrainRightSidebarContent wave={wave} /> : <BrainRightSidebarFollowers wave={wave} closeFollowers={() => setMode(Mode.CONTENT)} />;
      $[54] = mode;
      $[55] = setMode;
      $[56] = wave;
      $[57] = t17;
    } else {
      t17 = $[57];
    }
    let t18;
    if ($[58] !== t16 || $[59] !== t17) {
      t18 = <div className="tw-h-full tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-700">{t16}{t17}</div>;
      $[58] = t16;
      $[59] = t17;
      $[60] = t18;
    } else {
      t18 = $[60];
    }
    return t18;
  }
  let t16;
  if ($[61] !== setActiveTab) {
    t16 = key => setActiveTab(key as SidebarTab);
    $[61] = setActiveTab;
    $[62] = t16;
  } else {
    t16 = $[62];
  }
  let t17;
  if ($[63] !== activeTab || $[64] !== options || $[65] !== t16) {
    t17 = <div className="tw-pb-px tw-pl-2.5"><TabToggleWithOverflow options={options} activeKey={activeTab} onSelect={t16} maxVisibleTabs={3} /></div>;
    $[63] = activeTab;
    $[64] = options;
    $[65] = t16;
    $[66] = t17;
  } else {
    t17 = $[66];
  }
  const t18 = rankWaveComponents[activeTab];
  let t19;
  if ($[67] !== t18) {
    t19 = <div>{t18}</div>;
    $[67] = t18;
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  let t20;
  if ($[69] !== t17 || $[70] !== t19) {
    t20 = <>{t17}{t19}</>;
    $[69] = t17;
    $[70] = t19;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  return t20;
};